import { type ByteSource, viewOf } from './bytes';
import type { Movie, Track } from './mp4-read';

export const MAX_CHPL_CHAPTERS = 255;
const MAX_TITLE_BYTES = 96;
const CHPL_TICKS_PER_SECOND = 10_000_000;
const CHPL_VERSION_FLAGS = 0x01000000;
const ENCD_UTF8 = Uint8Array.of(0, 0, 0, 12, 0x65, 0x6e, 0x63, 0x64, 0, 0, 1, 0);

export interface Chapter {
  readonly start: number;
  readonly title: string;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function shortTitle(title: string): string {
  const clean = title.replace(/\s+/g, ' ').trim();
  if (encoder.encode(clean).length <= MAX_TITLE_BYTES) return clean;
  let cut = '';
  for (const char of clean) {
    if (encoder.encode(`${cut}${char}…`).length > MAX_TITLE_BYTES) break;
    cut += char;
  }
  return `${cut.trimEnd()}…`;
}

export function normalizeChapters(chapters: readonly Chapter[], duration: number): readonly Chapter[] {
  const sorted = chapters
    .filter((chapter) => Number.isFinite(chapter.start) && chapter.start < duration)
    .map((chapter) => ({ start: Math.max(0, chapter.start), title: chapter.title }))
    .sort((a, b) => a.start - b.start)
    .filter((chapter, i, all) => i === 0 || chapter.start > all[i - 1]!.start);
  const first = sorted[0];
  const fromZero = first && first.start > 0 ? [{ start: 0, title: first.title }, ...sorted.slice(1)] : sorted;
  return fromZero.map((chapter, i) => ({ start: chapter.start, title: shortTitle(chapter.title) || `Chapter ${i + 1}` }));
}

export const chapterEnd = (chapters: readonly Chapter[], index: number, duration: number): number =>
  chapters[index + 1]?.start ?? duration;

export function chplBody(chapters: readonly Chapter[]): Uint8Array | undefined {
  if (chapters.length === 0 || chapters.length > MAX_CHPL_CHAPTERS) return undefined;
  const titles = chapters.map((chapter) => encoder.encode(chapter.title));
  const out = new Uint8Array(9 + titles.reduce((sum, title) => sum + 9 + title.length, 0));
  const view = viewOf(out);
  view.setUint32(0, CHPL_VERSION_FLAGS);
  view.setUint8(8, chapters.length);
  let at = 9;
  chapters.forEach((chapter, i) => {
    view.setBigUint64(at, BigInt(Math.round(chapter.start * CHPL_TICKS_PER_SECOND)));
    view.setUint8(at + 8, titles[i]!.length);
    out.set(titles[i]!, at + 9);
    at += 9 + titles[i]!.length;
  });
  return out;
}

export function parseChpl(body: Uint8Array): readonly Chapter[] {
  const view = viewOf(body);
  const versioned = view.getUint8(0) === 1;
  const countAt = versioned ? 8 : 4;
  const chapters: Chapter[] = [];
  for (let i = 0, at = countAt + 1; i < (body[countAt] ?? 0) && at + 9 <= body.length; i++) {
    const length = view.getUint8(at + 8);
    chapters.push({
      start: Number(view.getBigUint64(at)) / CHPL_TICKS_PER_SECOND,
      title: decoder.decode(body.subarray(at + 9, at + 9 + length)),
    });
    at += 9 + length;
  }
  return chapters;
}

export function chapterSample(title: string): Uint8Array {
  const text = encoder.encode(title);
  const out = new Uint8Array(2 + text.length + ENCD_UTF8.length);
  viewOf(out).setUint16(0, text.length);
  out.set(text, 2);
  out.set(ENCD_UTF8, 2 + text.length);
  return out;
}

export function parseChapterSample(sample: Uint8Array): string {
  const length = sample.length >= 2 ? viewOf(sample).getUint16(0) : 0;
  return decoder.decode(sample.subarray(2, 2 + length));
}

const chapterTrackOf = (movie: Movie): Track | undefined => {
  const ids = new Set(movie.tracks.flatMap((track) => track.chapterTrackIds));
  return movie.tracks.find((track) => ids.has(track.id) && track.handler === 'text');
};

export async function readChapters(source: ByteSource, movie: Movie): Promise<readonly Chapter[]> {
  const track = chapterTrackOf(movie);
  if (!track) return movie.chpl ? parseChpl(movie.chpl) : [];
  const { offsets, sizes, times, count } = track.samples;
  const samples = await Promise.all(Array.from({ length: count }, (_, i) => source.read(offsets[i]!, sizes[i]!)));
  return samples.map((sample, i) => ({ start: times[i]!, title: parseChapterSample(sample) }));
}
